import { Produto } from '../../protocols';

/**
 * Arredonda um número para a quantidade de casas decimais informada.
 */
const roundTo = (value: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
};

/**
 * Calcula IBS/CBS e custos para um único produto, seguindo a lógica Delphi.
 */
const calcularIbsCbsProduto = (produto: Produto): Produto => {
  // trabalhamos em cópia para não mutar o objeto original
  const novoProduto: Produto = { ...produto };

  // Se não houver código IBS/CBS, zera campos relacionados e retorna
  if (novoProduto.cod_ibs_cbs <= 0) {
    return {
      ...novoProduto,
      val_bc_ibs_cbs: 0,
      val_bc_ibs_cbs_final: 0,
      val_cbs: 0,
      val_ibs_est: 0,
      val_ibs_mun: 0,
      val_imp_ibs_cbs: 0,
      val_custo_sibscbs: novoProduto?.val_custo_sibscbs || 0,
      val_custo_sibscbs_fisc: novoProduto?.val_custo_sibscbs_fisc || 0,
    };
  }

  let vPerAliqEfetiva = 0;

  const creditoIcms = novoProduto.val_imposto_credito || 0; // CREDITO_ICMS
  const valTotIcmsFcp = novoProduto.val_tot_icms_fcp || 0; // VAL_TOT_ICMS_FCP
  const valTabelaLiq = novoProduto.val_tabela_liq || 0; // VAL_TABELA_LIQ

  // === VAL_BC_IBS_CBS_FINAL ===
  if (novoProduto.val_bc_ibs_cbs > 0) {
    novoProduto.val_bc_ibs_cbs_final = novoProduto.val_bc_ibs_cbs;
  } else {
    novoProduto.val_bc_ibs_cbs_final =
      valTabelaLiq - (creditoIcms + valTotIcmsFcp);
  }

  const baseFinal = novoProduto.val_bc_ibs_cbs_final || 0;

  // === CBS (VAL_CBS) ===
  if (novoProduto.per_aliq_cbs > 0) {
    if (novoProduto.per_red_cbs > 0) {
      vPerAliqEfetiva =
        novoProduto.per_aliq_cbs -
        roundTo(novoProduto.per_aliq_cbs * (novoProduto.per_red_cbs / 100), 2);

      novoProduto.val_cbs = roundTo(baseFinal * (vPerAliqEfetiva / 100), 2);
    } else {
      novoProduto.val_cbs = roundTo(
        baseFinal * (novoProduto.per_aliq_cbs / 100),
        2,
      );
    }
  } else {
    novoProduto.val_cbs = 0;
  }

  // === IBS ESTADUAL (VAL_IBS_EST) ===
  if (novoProduto.per_aliq_ibs_est > 0) {
    if (novoProduto.per_red_ibs > 0) {
      vPerAliqEfetiva =
        novoProduto.per_aliq_ibs_est -
        roundTo(
          novoProduto.per_aliq_ibs_est * (novoProduto.per_red_ibs / 100),
          2,
        );

      novoProduto.val_ibs_est = roundTo(baseFinal * (vPerAliqEfetiva / 100), 2);
    } else {
      novoProduto.val_ibs_est = roundTo(
        baseFinal * (novoProduto.per_aliq_ibs_est / 100),
        2,
      );
    }
  } else {
    novoProduto.val_ibs_est = 0;
  }

  // === IBS MUNICIPAL (VAL_IBS_MUN) ===
  if (novoProduto.per_aliq_ibs_mun > 0) {
    if (novoProduto.per_red_ibs > 0) {
      vPerAliqEfetiva =
        novoProduto.per_aliq_ibs_mun -
        roundTo(
          novoProduto.per_aliq_ibs_mun * (novoProduto.per_red_ibs / 100),
          2,
        );

      novoProduto.val_ibs_mun = roundTo(baseFinal * (vPerAliqEfetiva / 100), 2);
    } else {
      novoProduto.val_ibs_mun = roundTo(
        baseFinal * (novoProduto.per_aliq_ibs_mun / 100),
        2,
      );
    }
  } else {
    novoProduto.val_ibs_mun = 0;
  }

  // === VAL_IMP_IBS_CBS (por unidade) ===
  const divisorQtde = novoProduto.qtd_total > 0 ? novoProduto.qtd_total : 1;

  novoProduto.val_imp_ibs_cbs = roundTo(
    (novoProduto.val_ibs_mun + novoProduto.val_ibs_est + novoProduto.val_cbs) /
      divisorQtde,
    4,
  );

  // === Ajuste de custo (VAL_CUSTO_SIBSCBS e VAL_CUSTO_SIBSCBS_FISC) ===
  novoProduto.val_custo_sibscbs = roundTo(
    (novoProduto.val_custo_sibscbs || 0) - novoProduto.val_imp_ibs_cbs,
    2,
  );

  novoProduto.val_custo_sibscbs_fisc = roundTo(
    (novoProduto.val_custo_sibscbs_fisc || 0) - novoProduto.val_imp_ibs_cbs,
    2,
  );

  return novoProduto;
};

/**
 * Aplica o cálculo IBS/CBS para um item único ou uma lista de produtos.
 * Sempre retorna array.
 */
export const calcularIbsCbsProdutos = (
  itens: Produto | Produto[],
): Produto[] => {
  const lista = Array.isArray(itens) ? itens : [itens];

  return lista.map((item) => calcularIbsCbsProduto(item));
};
